/* eslint react/prop-types: 0 */
import { darken, lighten, makeStyles } from '@material-ui/core/styles';
import { map, mapValues, sum, values } from 'lodash';

import { useStatusColor } from '../../../utilities/ColorUtilities';
import { orderedStatuses } from '../../../utilities/StatusUtilities';

// the radius for a circule with a circumference of 100. Makes our math easy.
// See: https://medium.com/@heyoka/scratch-made-svg-donut-pie-charts-in-html5-2c587e935d72
const RADIUS = 100 / (2 * Math.PI);

export default function SiteCluster(props) {
    const { sites, size } = props;

    if (!sites || !sites.length) {
        return null;
    }

    const totals = sites.reduce((t, meta) => mergeTotal(t, meta.status), buildInitialTotal());
    const total = sum(values(totals));
    const percents = mapValues(totals, (v) => (v / total) * 100);

    return <SvgCluster size={size} total={total} statusPercents={percents} />;
}

function mergeTotal(total, status) {
    // eslint-disable-next-line no-param-reassign
    total[status?.worst || 'gray'] += 1;
    return total;
}

function buildInitialTotal() {
    return orderedStatuses.reduce((acc, { color }) => {
        acc[color] = 0;
        return acc;
    }, {});
}

const useStyles = makeStyles((theme) => ({
    root: {
        filter: theme.palette.type === 'light' ? 'none' : 'brightness(80%)',
    },

    clusterCountText: {
        fontSize: '2em',
        fontWeight: 1000,
        textAnchor: 'middle',
        transform: 'translateY(0.35em)',
        fill: theme.palette.getContrastText(theme.palette.background.paper),
    },

    clusterCountOverlay: {
        fill:
            theme.palette.type === 'light'
                ? darken(theme.palette.background.paper, 0.05)
                : lighten(theme.palette.background.paper, 0.15),
    },
}));

export function SvgCluster(props) {
    const { size, statusPercents, total } = props;
    const classes = useStyles();

    const segments = [];
    let offset = 25; // start at 12 'o clock, not 3

    map(statusPercents, (percent, status) => {
        if (percent === 0) {
            return null;
        }

        segments.push(<SvgClusterSegment key={status} status={status} value={percent} offset={offset} />);

        offset -= percent;
    });

    return (
        <svg width={`${size}px`} height={`${size}px`} viewBox="0 0 100 100" className={classes.root}>
            {segments}

            <circle className={classes.clusterCountOverlay} cx="50" cy="50" r={RADIUS * 1.6} />

            <text className={classes.clusterCountText} x="50%" y="50%">
                {total}
            </text>
        </svg>
    );
}

function SvgClusterSegment(props) {
    const { status, value, offset } = props;

    const statusColor = useStatusColor(status);

    return (
        <circle
            cx="50"
            cy="50"
            r={RADIUS}
            fill="transparent"
            stroke={statusColor}
            strokeOpacity={1}
            strokeWidth={45}
            strokeDasharray={`${value} ${100 - value}`}
            strokeDashoffset={offset}
        />
    );
}
